import { AnnotationPad } from './AnnotationPad';
import RichTextView from './RichTextView';
import { hasDescriptiveContent, normalizeDescriptive } from '../utils/answers';
import type {
  AnnotationMap, AnswerPayload, DescriptiveAnswer, McqAnswer, ReviewBundle, ReviewQuestion,
} from '../types/models';

/** Parse a stored answer payload (descriptive or mcq), tolerating malformed JSON. */
export function parseAnswer(json: string | null): AnswerPayload | null {
  if (!json) return null;
  try {
    const p = JSON.parse(json);
    if (p?.kind === 'descriptive') return normalizeDescriptive(p);
    return p?.kind === 'mcq' ? p : null;
  } catch {
    return null;
  }
}

/** Parse a stored annotation map ({ pageId: Stroke[] }), tolerating malformed JSON. */
export function parseAnnotations(json: string | null | undefined): AnnotationMap {
  if (!json) return {};
  try {
    const o = JSON.parse(json);
    return o && typeof o === 'object' && !Array.isArray(o) ? (o as AnnotationMap) : {};
  } catch {
    return {};
  }
}

/** Auto-score a single MCQ: full marks only when the chosen set equals the correct set. */
export function mcqScore(q: ReviewQuestion): number {
  if (q.type === 'Descriptive') return 0;
  const a = parseAnswer(q.answerJson);
  if (!a || a.kind !== 'mcq') return 0;
  const chosen = new Set((a as McqAnswer).selectedOptionIds);
  const correct = new Set(q.options.filter(o => o.isCorrect).map(o => o.id));
  if (chosen.size !== correct.size) return 0;
  for (const id of chosen) if (!correct.has(id)) return 0;
  return q.marks;
}

/** Sub-part label: (a), (b), (c)… */
const partLetter = (i: number) => String.fromCharCode(97 + i);

/** Grading hooks keyed by the gradable unit id (a question id, or a part id for multi-part). */
type Grading = { valueOf: (unitId: string) => number; onChange: (unitId: string, n: number) => void };

/** Red-pen annotation for one question: the strokes plus (in edit mode) a sink. onChange absent = read-only. */
export type AnnotationHook = { value: AnnotationMap; onChange?: (m: AnnotationMap) => void };

/** Per-question remark: the text plus (in edit mode) a sink. onChange absent = read-only. */
export type RemarkHook = { value: string; onChange?: (s: string) => void };

/**
 * Assigns a display number to each "unit": a standalone question gets its own number, and an
 * either/or pair shares one number (with A/B labels). Preserves question order.
 */
export function reviewUnitInfo(questions: ReviewQuestion[]) {
  const map = new Map<string, { unit: number; alt?: 'A' | 'B'; choice: boolean }>();
  const groupUnit = new Map<string, number>();
  let unit = 0;
  for (const q of questions) {
    if (q.choiceGroupId) {
      let u = groupUnit.get(q.choiceGroupId);
      if (u === undefined) { u = ++unit; groupUnit.set(q.choiceGroupId, u); }
      map.set(q.id, { unit: u, alt: q.choiceOrder === 0 ? 'A' : 'B', choice: true });
    } else {
      map.set(q.id, { unit: ++unit, choice: false });
    }
  }
  return map;
}

/** The grader's marks control — sits to the right of the answer, with − / + steppers and an
 *  optional MCQ auto-score shortcut. */
function MarksStepper({ max, value, auto, onChange }:
  { max: number; value: number; auto: number | null; onChange: (n: number) => void }) {
  const set = (n: number) => onChange(Math.max(0, Math.min(max, Math.round(n))));
  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', textAlign: 'center',
      background: 'var(--surface-2)',
    }}>
      <div className="muted" style={{ fontSize: 12, fontWeight: 600 }}>Marks awarded</div>
      <div className="row" style={{ gap: 6, alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>
        <button type="button" className="mark-step" onClick={() => set(value - 1)} disabled={value <= 0}
                aria-label="Decrease mark" style={{ width: 32, padding: '4px 0' }}>−</button>
        <input type="number" min={0} max={max} value={value} style={{ width: 64, textAlign: 'center' }}
               onChange={e => set(Number(e.target.value) || 0)} />
        <button type="button" className="mark-step" onClick={() => set(value + 1)} disabled={value >= max}
                aria-label="Increase mark" style={{ width: 32, padding: '4px 0' }}>+</button>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>out of {max}</div>
      {auto !== null && (
        <button type="button" className="link-btn" style={{ marginTop: 4 }}
                onClick={() => onChange(auto)}>auto: {auto}</button>
      )}
    </div>
  );
}

/** Read-only "Awarded x / max" box (student view), right-aligned like the grading stepper. */
function AwardedBox({ awarded, max }: { awarded: number; max: number }) {
  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', textAlign: 'center',
      background: 'var(--surface-2)', minWidth: 120,
    }}>
      <div className="muted" style={{ fontSize: 12, fontWeight: 600 }}>Awarded</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>{awarded} <span className="muted" style={{ fontSize: 13 }}>/ {max}</span></div>
    </div>
  );
}

/** The marks control for one gradable unit: an editable stepper while grading, else the read-only
 *  awarded box (or nothing if ungraded). */
function marksControl(unitId: string, max: number, grading: Grading | undefined,
                      awarded: number | null, auto: number | null) {
  if (grading) return <MarksStepper max={max} value={grading.valueOf(unitId)} auto={auto}
                                    onChange={n => grading.onChange(unitId, n)} />;
  return awarded != null ? <AwardedBox awarded={awarded} max={max} /> : null;
}

/** Answer on the left; a right-hand column stacking the marks and (beneath it) the remark. */
function AnswerRow({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="row" style={{ gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 12 }}>
      <div style={{ flex: '1 1 300px', minWidth: 0 }}>{children}</div>
      {right && (
        <div style={{ flex: '0 0 220px', maxWidth: 240, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {right}
        </div>
      )}
    </div>
  );
}

/** The per-part answer + marks for a multi-part descriptive question. Marks sit to the right of
 *  each part; the red-pen annotation layer is shared across the whole question (keyed by page id). */
function PartsReview({ question, answer, grading, annotation }:
  { question: ReviewQuestion; answer: DescriptiveAnswer | null; grading?: Grading; annotation?: AnnotationHook }) {
  const parts = question.parts ?? [];
  const byId = new Map((answer?.parts ?? []).map(p => [p.partId, p]));
  return (
    <div style={{ marginTop: 12 }}>
      {parts.map((part, i) => {
        const slot = byId.get(part.id) ?? null;
        const slotAnswer: DescriptiveAnswer | null =
          slot ? { kind: 'descriptive', pages: slot.pages, blocks: slot.blocks } : null;
        const answered = !!slot && hasDescriptiveContent(slot);
        return (
          <div key={part.id} className="card"
               style={{ background: 'var(--surface-2)', marginBottom: 12, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div style={{ fontWeight: 700 }}>({partLetter(i)})</div>
              <div className="muted" style={{ fontSize: 12 }}>{part.marks} mark{part.marks > 1 ? 's' : ''}</div>
            </div>
            <RichTextView html={part.prompt} style={{ margin: '6px 0 10px' }} />
            {answered ? (
              <AnswerRow right={marksControl(part.id, part.marks, grading, part.awardedPoints, null)}>
                <DescriptiveReview answer={slotAnswer} annotation={annotation} />
              </AnswerRow>
            ) : (
              <DescriptiveReview answer={slotAnswer} annotation={annotation} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Just the student's answer body (handles MCQ / descriptive / multi-part) — no marks/rubric
 *  chrome. Used by the redesigned result page's compact question cards; shows saved red-pen
 *  corrections and the evaluator's remark read-only. */
export function AnswerBody({ question }: { question: ReviewQuestion }) {
  const a = parseAnswer(question.answerJson);
  const hasParts = (question.parts?.length ?? 0) > 0;
  const annotation: AnnotationHook = { value: parseAnnotations(question.annotationJson) };
  return (
    <>
      {hasParts
        ? <PartsReview question={question} answer={a as DescriptiveAnswer | null} annotation={annotation} />
        : question.type === 'Descriptive'
          ? <DescriptiveReview answer={a as DescriptiveAnswer | null} annotation={annotation} />
          : <McqReview question={question} answer={a as McqAnswer | null} />}
      {question.graderRemark && <RemarkView text={question.graderRemark} />}
    </>
  );
}

export function QuestionReview({ index, question, altLabel, grading, annotation, remark }:
  { index: number; question: ReviewQuestion; altLabel?: 'A' | 'B';
    grading?: Grading; annotation?: AnnotationHook; remark?: RemarkHook }) {
  const a = parseAnswer(question.answerJson);
  const hasParts = (question.parts?.length ?? 0) > 0;
  const auto = question.type !== 'Descriptive' ? mcqScore(question) : null;
  // Whether the student actually submitted an answer — no answer means no marks to award.
  const answered = a != null && (
    a.kind === 'mcq'
      ? a.selectedOptionIds.length > 0
      : hasDescriptiveContent(a));
  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontWeight: 600, flex: 1 }}>
          Q{index + 1}.{' '}
          {altLabel && (
            <span style={{
              fontSize: 12, fontWeight: 700, color: 'var(--accent)',
              border: '1px solid var(--accent)', borderRadius: 6, padding: '1px 6px', marginRight: 6,
            }}>Either/Or · {altLabel}</span>
          )}
          <RichTextView html={question.prompt} style={{ fontWeight: 400, display: 'inline-block', width: '100%' }} />
        </div>
        <div className="muted" style={{ whiteSpace: 'nowrap', marginLeft: 16 }}>
          {question.marks} mark{question.marks > 1 ? 's' : ''}
        </div>
      </div>

      {hasParts ? (
        <>
          <PartsReview question={question} answer={a as DescriptiveAnswer | null} grading={grading} annotation={annotation} />
          {/* One remark for the whole multi-part question, aligned under the marks column. */}
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 4 }}>
            <div style={{ flex: '0 0 220px', maxWidth: 240 }}><RemarkBlock remark={remark} /></div>
          </div>
        </>
      ) : (
        <AnswerRow right={<>
          {answered && marksControl(question.id, question.marks, grading, question.awardedPoints, auto)}
          <RemarkBlock remark={remark} />
        </>}>
          {question.type === 'Descriptive'
            ? <DescriptiveReview answer={a as DescriptiveAnswer | null} annotation={annotation} />
            : <McqReview question={question} answer={a as McqAnswer | null} />}
        </AnswerRow>
      )}

      {question.rubric && (
        <div style={{
          marginTop: 12,
          padding: '10px 12px',
          background: 'var(--warn-soft-bg)',
          color: 'var(--warn-soft-fg)',
          border: '1px solid var(--warn-border)',
          borderRadius: 8,
          fontSize: 13
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Rubric — {question.rubric.totalMaxPoints} pts
            {question.rubric.aiAssistedGrading ? ' · AI-assisted' : ''}
          </div>
          {question.rubric.description && (
            <div style={{ marginBottom: 6 }}>{question.rubric.description}</div>
          )}
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {question.rubric.criteria.map(c => (
              <li key={c.id}>
                <strong>[{c.category}] {c.title}</strong> — {c.maxPoints} pts
                {c.description && <> · {c.description}</>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Editable remark box (grading) or read-only remark line (when a remark exists). */
function RemarkBlock({ remark }: { remark?: RemarkHook }) {
  if (remark?.onChange) {
    return (
      <label style={{ display: 'block', marginTop: 12 }}>
        <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Remark for this question (optional)</div>
        <textarea rows={2} value={remark.value}
                  onChange={e => remark.onChange!(e.target.value)}
                  placeholder="Feedback the student will see for this question…" />
      </label>
    );
  }
  return remark?.value ? <RemarkView text={remark.value} /> : null;
}

/** Read-only remark, shown to students and in review once saved. */
function RemarkView({ text }: { text: string }) {
  return (
    <div style={{
      marginTop: 12, padding: '8px 12px', borderRadius: 8,
      background: 'var(--accent-soft-bg)', color: 'var(--accent)', fontSize: 13, whiteSpace: 'pre-wrap',
    }}>
      <strong>Remark:</strong> {text}
    </div>
  );
}

function McqReview({ question, answer }: { question: ReviewQuestion; answer: McqAnswer | null }) {
  const selected = new Set(answer?.selectedOptionIds ?? []);
  return (
    <div>
      {question.options.map(o => {
        const chose = selected.has(o.id);
        const bg = o.isCorrect
          ? 'var(--success-soft-bg)'
          : chose ? 'var(--danger-soft-bg)' : undefined;
        const fg = o.isCorrect
          ? 'var(--success-soft-fg)'
          : chose ? 'var(--danger-soft-fg)' : undefined;
        const label = o.isCorrect ? '✓ correct' : chose ? '✗ chosen' : '';
        return (
          <div key={o.id} className="mcq-option" style={{ background: bg, color: fg, cursor: 'default' }}>
            <input type="checkbox" checked={chose} readOnly style={{ width: 'auto', marginTop: 2 }} />
            <span style={{ flex: 1 }}>{o.text}</span>
            {label && <span className="muted" style={{ fontSize: 12 }}>{label}</span>}
          </div>
        );
      })}
      {!answer && <div className="muted">No answer submitted.</div>}
    </div>
  );
}

/**
 * Section-wise scored/max breakdown plus the grand total. `scoreOf` resolves a question's total
 * scored marks (for a multi-part question, the sum across its parts).
 */
export function MarksSummary({ bundle, scoreOf }: { bundle: ReviewBundle; scoreOf: (questionId: string) => number }) {
  const right = { textAlign: 'right' as const };
  const rows = bundle.sections.map(sec => {
    const qs = bundle.questions.filter(q => q.sectionId === sec.id);
    return { id: sec.id, title: sec.title, scored: qs.reduce((s, q) => s + scoreOf(q.id), 0), max: sec.maxMarks };
  });
  const loose = bundle.questions.filter(q => !q.sectionId);
  const looseScored = loose.reduce((s, q) => s + scoreOf(q.id), 0);
  const looseMax = loose.reduce((s, q) => s + q.marks, 0);
  const total = rows.reduce((s, r) => s + r.scored, 0) + looseScored;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Marks summary</h3>
      <table className="data-table">
        <thead>
          <tr><th>Section</th><th style={right}>Scored</th><th style={right}>Max</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}><td>{r.title}</td><td style={right}>{r.scored}</td><td style={right}>{r.max}</td></tr>
          ))}
          {loose.length > 0 && (
            <tr><td>Other questions</td><td style={right}>{looseScored}</td><td style={right}>{looseMax}</td></tr>
          )}
          <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
            <td>Total</td><td style={right}>{total}</td><td style={right}>{bundle.totalMarks}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function DescriptiveReview({ answer, annotation }:
  { answer: DescriptiveAnswer | null; annotation?: AnnotationHook }) {
  if (!answer) return <div className="muted">No answer submitted.</div>;
  const hasInk = answer.pages.some(p => p.strokes.length > 0);
  const hasText = answer.blocks.some(b => b.value.trim().length > 0);
  if (!hasInk && !hasText) return <div className="muted">No answer submitted.</div>;
  return (
    <div className="answer-editor">
      {hasInk && (
        <AnnotationPad
          pages={answer.pages}
          annotations={annotation?.value ?? {}}
          onChange={annotation?.onChange}
          readOnly={!annotation?.onChange}
        />
      )}
      {answer.blocks.map(b => (
        <div key={b.id} className="answer-block">
          <div className="block-toolbar"><span>Typed</span></div>
          <div style={{ padding: 12, whiteSpace: 'pre-wrap', minHeight: 40 }}>
            {b.value || <span className="muted">(empty)</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
